import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { User } from "oidc-client-ts";
import { identityFromUser, userManager, type Identity } from "../lib/auth";
import { setAccessToken } from "../lib/frappe";

export type AuthStatus = "loading" | "anonymous" | "authenticated";

type AuthContextValue = {
  status: AuthStatus;
  identity: Identity | null;
  /** Start the PKCE redirect; `returnTo` is restored after the callback. */
  signIn: (returnTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Called by the /auth/callback route; resolves to the path to continue to. */
  completeSignIn: () => Promise<string>;
  hasRole: (role: string) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [identity, setIdentity] = useState<Identity | null>(null);

  const apply = useCallback((user: User | null) => {
    if (user && !user.expired) {
      setAccessToken(user.access_token);
      setIdentity(identityFromUser(user));
      setStatus("authenticated");
    } else {
      setAccessToken(null);
      setIdentity(null);
      setStatus("anonymous");
    }
  }, []);

  useEffect(() => {
    const um = userManager();
    let cancelled = false;
    um.getUser().then((u) => { if (!cancelled) apply(u); }).catch(() => { if (!cancelled) apply(null); });
    const onLoaded = (u: User) => apply(u);
    const onUnloaded = () => apply(null);
    const onExpired = () => apply(null);
    const onRenewError = () => apply(null);
    um.events.addUserLoaded(onLoaded);
    um.events.addUserUnloaded(onUnloaded);
    um.events.addAccessTokenExpired(onExpired);
    um.events.addSilentRenewError(onRenewError);
    return () => {
      cancelled = true;
      um.events.removeUserLoaded(onLoaded);
      um.events.removeUserUnloaded(onUnloaded);
      um.events.removeAccessTokenExpired(onExpired);
      um.events.removeSilentRenewError(onRenewError);
    };
  }, [apply]);

  const value = useMemo<AuthContextValue>(() => ({
    status,
    identity,
    signIn: (returnTo) => userManager().signinRedirect({ state: { returnTo: returnTo ?? window.location.pathname } }),
    signOut: async () => {
      setAccessToken(null);
      await userManager().signoutRedirect();
    },
    completeSignIn: async () => {
      const user = await userManager().signinRedirectCallback();
      apply(user);
      const state = user.state as { returnTo?: string } | undefined;
      const to = state?.returnTo && state.returnTo.startsWith("/") && !state.returnTo.startsWith("/auth") ? state.returnTo : "/";
      return to;
    },
    hasRole: (role) => !!identity?.roles.includes(role),
  }), [status, identity, apply]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
